import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, LogOut, Loader2, ClipboardList, X, ShieldOff, UserMinus } from "lucide-react";
import { api } from "@/lib/api";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("ady_admin_token")}` });

// Order and keys mirror INTAKE_FIELDS in backend/server.py.
const INTAKE_LABELS = {
  full_name: "Name",
  age: "Age",
  height_cm: "Height (cm)",
  weight_kg: "Weight (kg)",
  experience: "Training Experience",
  days_per_week: "Days / Week",
  equipment: "Gym / Equipment",
  goal: "Primary Goal",
  injuries: "Injuries & Conditions",
  diet: "Dietary Restrictions",
  timezone: "Timezone",
  preferred_slot: "Preferred Call Slot",
  notes: "Anything Else",
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - Date.now()) / 86400000);
  return Number.isFinite(d) ? d : null;
};

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("ady_admin_token", data.token);
      onLogin();
    } catch {
      toast.error("Invalid password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="card-ady p-10 w-full max-w-sm" data-testid="admin-login-form">
        <h1 className="font-anton text-2xl uppercase neon-text mb-8">Admin Access</h1>
        <input
          data-testid="admin-password-input"
          type="password"
          className="input-ady mb-6"
          placeholder="PASSWORD"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button data-testid="admin-login-btn" disabled={loading} className="btn-brutal w-full">
          {loading ? <Loader2 className="animate-spin" size={18} /> : null} Enter
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ label, value, testId }) => (
  <div className="card-ady p-6" data-testid={testId}>
    <p className="font-jet text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-2">{label}</p>
    <p className="font-anton text-3xl text-[#39FF14]">{value}</p>
  </div>
);

const Badge = ({ status }) => {
  const paid = status === "paid";
  return (
    <span className={`font-jet text-[10px] px-2 py-1 border ${paid ? "border-[#39FF14] text-[#39FF14]" : "border-[#D90429] text-[#D90429]"}`}>
      {status?.toUpperCase()}
    </span>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("ady_admin_token"));
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [newT, setNewT] = useState({ name: "", text: "", rating: 5 });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  // Intake answers are fetched one client at a time — deliberately never bulk-loaded.
  const [intake, setIntake] = useState(null); // { orderId, email, data }
  const [intakeLoading, setIntakeLoading] = useState(null);

  const logout = () => {
    localStorage.removeItem("ady_admin_token");
    setAuthed(false);
  };

  const load = useCallback(async () => {
    try {
      const h = { headers: authHeaders() };
      const [s, o, m, t] = await Promise.all([
        api.get("/admin/stats", h),
        api.get("/admin/orders", h),
        api.get("/admin/messages", h),
        api.get("/testimonials"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setMessages(m.data);
      setTestimonials(t.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const deleteOrder = async (id) => {
    await api.delete(`/admin/orders/${id}`, { headers: authHeaders() });
    toast.success("Order deleted");
    load();
  };

  const viewIntake = async (order) => {
    setIntakeLoading(order.id);
    try {
      const { data } = await api.get(`/admin/orders/${order.id}/intake`, { headers: authHeaders() });
      setIntake({ orderId: order.id, email: order.email, data });
    } catch (e) {
      toast.error(e.response?.status === 404 ? "This client hasn't submitted their intake yet." : "Could not load intake");
    }
    setIntakeLoading(null);
  };

  const deleteIntake = async (orderId) => {
    if (!window.confirm("Permanently delete this client's health data? The order itself is kept.")) return;
    try {
      await api.delete(`/admin/orders/${orderId}/intake`, { headers: authHeaders() });
      toast.success("Health data deleted");
      setIntake(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const releaseSpot = async (sessionId) => {
    if (!window.confirm("Free this coaching spot? The client keeps their order — this only makes the slot available again.")) return;
    try {
      await api.post(`/admin/consultation/${encodeURIComponent(sessionId)}/release`, {}, { headers: authHeaders() });
      toast.success("Spot released");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    if (!newT.name || !newT.text) return toast.error("Name and text required");
    await api.post("/admin/testimonials", { ...newT, rating: Number(newT.rating) }, { headers: authHeaders() });
    setNewT({ name: "", text: "", rating: 5 });
    toast.success("Testimonial added");
    load();
  };

  const deleteTestimonial = async (id) => {
    await api.delete(`/admin/testimonials/${id}`, { headers: authHeaders() });
    toast.success("Testimonial deleted");
    load();
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/change-password", pw, { headers: authHeaders() });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-10">
        <h1 className="font-anton text-3xl uppercase neon-text">Command Center</h1>
        <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 font-jet text-xs text-[#A1A1AA] hover:text-[#D90429]">
          <LogOut size={16} /> LOGOUT
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="TOTAL ORDERS" value={stats.total_orders} testId="stat-total-orders" />
          <StatCard label="PAID ORDERS" value={stats.paid_orders} testId="stat-paid-orders" />
          <StatCard label="MESSAGES" value={stats.messages} testId="stat-messages" />
          <StatCard label="REVENUE" value={`€${stats.revenue.toFixed(2)}`} testId="stat-revenue" />
        </div>
      )}

      <div className="flex gap-1 mb-8 border-b border-[#2A2A2A] overflow-x-auto whitespace-nowrap">
        {["orders", "coaching", "messages", "testimonials", "settings"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`font-bebas tracking-[0.2em] px-5 py-3 text-sm ${tab === t ? "text-[#39FF14] border-b-2 border-[#39FF14]" : "text-[#A1A1AA]"}`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="overflow-x-auto card-ady" data-testid="admin-orders-table">
          <table className="w-full text-left font-jet text-xs">
            <thead className="border-b border-[#2A2A2A] text-[#A1A1AA]">
              <tr>
                {["DATE", "PROVIDER", "EMAIL", "AMOUNT", "STATUS", "VIP CODE", "EMAIL SENT", ""].map((h) => (
                  <th key={h} className="p-4 tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#1A1A1A] hover:bg-[#0A0A0A]">
                  <td className="p-4 text-[#A1A1AA]">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-4 uppercase">{o.provider}</td>
                  <td className="p-4">{o.email}</td>
                  <td className="p-4">€{o.amount?.toFixed(2)}</td>
                  <td className="p-4"><Badge status={o.payment_status} /></td>
                  <td className="p-4 text-[#39FF14]">{o.vip_code || "—"}</td>
                  <td className="p-4">{o.email_sent ? "✓" : "—"}</td>
                  <td className="p-4">
                    <button data-testid={`delete-order-${o.id}`} onClick={() => deleteOrder(o.id)} className="text-[#52525B] hover:text-[#D90429]">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
              {!orders.length && (
                <tr><td colSpan={8} className="p-8 text-center text-[#52525B]">No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "coaching" && (
        <div className="space-y-5" data-testid="admin-coaching-list">
          {orders
            .filter((o) => o.product_id === "consultation" && o.payment_status === "paid")
            .map((o) => {
              const left = daysUntil(o.coaching_expires_at);
              const active = o.coaching_status === "active" && left !== null && left > 0;
              return (
                <div key={o.id} className="card-ady p-6" data-testid={`coaching-client-${o.id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-3">
                    <div>
                      <p className="font-bebas text-lg tracking-[0.15em] text-[#F0C800]">{o.email}</p>
                      <p className="font-jet text-[10px] text-[#52525B] tracking-widest mt-1">
                        VIP {o.vip_code || "—"} · €{o.amount?.toFixed(2)} · {o.provider?.toUpperCase()}
                      </p>
                    </div>
                    <span className={`font-jet text-[10px] px-2 py-1 border ${active ? "border-[#39FF14] text-[#39FF14]" : "border-[#52525B] text-[#52525B]"}`}>
                      {active ? `WEEK ${Math.min(4, 5 - Math.ceil(left / 7))} · ${left}D LEFT` : (o.coaching_status || "ended").toUpperCase()}
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-3 mt-5 font-jet text-[10px] tracking-widest text-[#A1A1AA]">
                    <div><span className="text-[#52525B]">BLOCK START</span><br />{fmtDate(o.coaching_started_at)}</div>
                    <div><span className="text-[#52525B]">BLOCK END</span><br />{fmtDate(o.coaching_expires_at)}</div>
                    <div>
                      <span className="text-[#52525B]">OVERSOLD</span><br />
                      {o.oversold ? <span className="text-[#D90429]">YES — CHECK CAPACITY</span> : "no"}
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6 flex-wrap">
                    <button
                      data-testid={`view-intake-${o.id}`}
                      onClick={() => viewIntake(o)}
                      disabled={intakeLoading === o.id || !!o.intake_purged_at}
                      className="btn-brutal btn-brutal-yellow !py-2 !px-5 !text-sm"
                    >
                      {intakeLoading === o.id ? <Loader2 className="animate-spin" size={15} /> : <ClipboardList size={15} />}
                      {o.intake_purged_at ? "Intake Deleted" : "View Intake"}
                    </button>
                    {active && (
                      <button
                        data-testid={`release-spot-${o.id}`}
                        onClick={() => releaseSpot(o.session_id)}
                        className="font-jet text-[10px] tracking-widest text-[#52525B] hover:text-[#D90429] flex items-center gap-2"
                      >
                        <UserMinus size={14} /> RELEASE SPOT
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          {!orders.filter((o) => o.product_id === "consultation" && o.payment_status === "paid").length && (
            <p className="text-center text-[#52525B] font-jet text-xs py-10">No coaching clients yet</p>
          )}
        </div>
      )}

      {intake && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          onClick={() => setIntake(null)}
          data-testid="intake-modal"
        >
          <div
            className="bg-[#111111] border-2 border-[#F0C800] w-full max-w-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start p-6 border-b border-[#2A2A2A]">
              <div>
                <p className="font-jet text-[10px] tracking-[0.3em] text-[#F0C800]">// CLIENT INTAKE //</p>
                <p className="font-bebas text-xl tracking-[0.15em] text-[#F5F5F5] mt-1">{intake.email}</p>
                <p className="font-jet text-[10px] text-[#52525B] mt-1">
                  Submitted {String(intake.data.submitted_at).slice(0, 16).replace("T", " ")}
                  {" · "}consent {intake.data.health_consent ? "given" : "MISSING"}
                </p>
              </div>
              <button onClick={() => setIntake(null)} className="text-[#52525B] hover:text-[#F5F5F5]" data-testid="close-intake">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {Object.keys(INTAKE_LABELS).map((k) => {
                const v = intake.data.answers?.[k];
                const isInjury = k === "injuries";
                return (
                  <div key={k} className={isInjury ? "border border-dashed border-[#D90429] p-4" : ""}>
                    <p className={`font-jet text-[10px] tracking-[0.25em] uppercase mb-1 ${isInjury ? "text-[#D90429]" : "text-[#39FF14]"}`}>
                      {INTAKE_LABELS[k]}
                    </p>
                    <p className="text-sm text-[#F5F5F5] whitespace-pre-wrap break-words">
                      {v && String(v).trim() ? v : <span className="text-[#52525B]">— not answered —</span>}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="p-6 border-t border-[#2A2A2A] flex justify-between items-center flex-wrap gap-4">
              <p className="font-jet text-[10px] text-[#52525B] tracking-widest">
                AUTO-DELETES {fmtDate(intake.data.purge_at)}
                {daysUntil(intake.data.purge_at) !== null && ` · IN ${daysUntil(intake.data.purge_at)} DAYS`}
              </p>
              <button
                data-testid="delete-intake-btn"
                onClick={() => deleteIntake(intake.orderId)}
                className="font-jet text-[10px] tracking-widest text-[#52525B] hover:text-[#D90429] flex items-center gap-2"
              >
                <ShieldOff size={14} /> DELETE HEALTH DATA NOW
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-4" data-testid="admin-messages-list">
          {messages.map((m) => (
            <div key={m.id} className="card-ady p-6">
              <div className="flex justify-between flex-wrap gap-2 mb-3">
                <p className="font-bebas tracking-[0.15em] text-[#39FF14]">{m.name} <span className="text-[#A1A1AA] font-jet text-xs ml-2">{m.email}</span></p>
                <p className="font-jet text-[10px] text-[#52525B]">{m.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
              <p className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
          {!messages.length && <p className="text-center text-[#52525B] font-jet text-xs py-10">No messages yet</p>}
        </div>
      )}

      {tab === "testimonials" && (
        <div className="grid lg:grid-cols-2 gap-8">
          <form onSubmit={addTestimonial} className="card-ady p-8 space-y-5 h-fit" data-testid="admin-testimonial-form">
            <h3 className="font-bebas text-xl tracking-[0.15em]">ADD TESTIMONIAL</h3>
            <input data-testid="testimonial-name-input" className="input-ady" placeholder="NAME" value={newT.name} onChange={(e) => setNewT({ ...newT, name: e.target.value })} />
            <textarea data-testid="testimonial-text-input" className="input-ady min-h-[100px]" placeholder="REVIEW TEXT" value={newT.text} onChange={(e) => setNewT({ ...newT, text: e.target.value })} />
            <select data-testid="testimonial-rating-select" className="input-ady" value={newT.rating} onChange={(e) => setNewT({ ...newT, rating: e.target.value })}>
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} STARS</option>)}
            </select>
            <button data-testid="testimonial-submit-btn" className="btn-brutal btn-brutal-green w-full">Add</button>
          </form>
          <div className="space-y-4">
            {testimonials.map((t) => (
              <div key={t.id} className="card-ady p-5 flex justify-between gap-4">
                <div>
                  <p className="font-bebas tracking-[0.15em] text-[#39FF14]">{t.name} <span className="text-[#A1A1AA] font-jet text-[10px]">({t.rating}★)</span></p>
                  <p className="text-xs text-[#A1A1AA] mt-2">{t.text}</p>
                </div>
                <button data-testid={`delete-testimonial-${t.id}`} onClick={() => deleteTestimonial(t.id)} className="text-[#52525B] hover:text-[#D90429] flex-shrink-0">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <form onSubmit={changePassword} className="card-ady p-8 max-w-md space-y-5" data-testid="admin-password-form">
          <h3 className="font-bebas text-xl tracking-[0.15em]">CHANGE PASSWORD</h3>
          <input data-testid="current-password-input" type="password" className="input-ady" placeholder="CURRENT PASSWORD" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
          <input data-testid="new-password-input" type="password" className="input-ady" placeholder="NEW PASSWORD (MIN 8 CHARS)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          <button data-testid="change-password-btn" className="btn-brutal w-full">Update Password</button>
        </form>
      )}
    </div>
  );
}
